import { AttendanceWrapper, AttendanceType } from './type';

function normalizeDate(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

const isSaturday = (dateStr: string): boolean => {
  if (!dateStr) return false; // 빈 값 방어 코드
  const date = new Date(dateStr);
  return date.getDay() === 6; // 6이면 토요일
};

const getDailyWorktime = (dateStr: string) => {
  const [hourStr, minuteStr] = dateStr.split(':');
  const hour = parseInt(hourStr, 10);
  return hour;
};

function getMostFrequentWorkHour(times: string[]): number | undefined {
  const workHourMap = new Map<number, number>();

  times.forEach((time) => {
    const [hourStr, minuteStr] = time.split(':');
    const hour = parseInt(hourStr, 10);

    if (hour === 0) return; // 시간 값이 0이면 무시

    workHourMap.set(hour, (workHourMap.get(hour) || 0) + 1);
  });

  let mostFrequentHour: number | undefined = undefined;
  let maxCount = 0;

  workHourMap.forEach((count, hour) => {
    if (count > maxCount) {
      maxCount = count;
      mostFrequentHour = hour;
    }
  });

  return mostFrequentHour;
}

export function totalDateFn(wrapper: AttendanceWrapper, isPartTime?: boolean) {
  const datas: AttendanceType[] = wrapper.data;

  const paidToUnpaidCountList: number[] = [];
  let paidToUnpaidCount: number = 0;
  const workHoursArray: string[] = [];

  let saturdayCnt: number = 0;

  const workdayIndecies: number[] = datas.reduce<number[]>(
    (prev, data, index) => {
      workHoursArray.push(data.workdayWorkTime);

      const date = new Date(data.date);
      const dayOfWeek = date.getDay(); // 0: 일, 1: 월, ..., 6: 토

      // 월~금(1~5)에 해당할 경우만 추가
      if (dayOfWeek >= 1 && dayOfWeek <= 5) {
        prev.push(index);
      }

      // 토요일이고 시간제라면 카운트
      if (dayOfWeek === 6) {
        saturdayCnt++;
      }

      return prev;
    },
    []
  );

  let totalCount = 0;
  let absentCount = 0;
  let consecutiveCount = 0;
  let includeAbsent = false;

  const groupTopIndex: number[] = [];
  const groupMiddleIndex: number[] = [];
  const groupBottomIndex: number[] = [];

  const absentGroupTopIndex: number[] = [];
  const absentGroupMiddleIndex: number[] = [];
  const absentGroupBottomIndex: number[] = [];

  const absentIndex: number[] = [];

  const paidSundayIndexList: number[] = []; // ✅ 유급처리되는 일요일
  const absentSundayIndexList: number[] = []; // ✅ 결근이 포함된 일요일

  workdayIndecies.forEach((index, i) => {
    const clockInStatus = datas[index]?.clockInStatus;
    const clockOutStatus = datas[index]?.clockOutStatus;

    const isAbsent =
      clockInStatus?.trim()?.startsWith('결근') ||
      clockOutStatus?.trim()?.startsWith('결근');
    if (isAbsent) {
      absentCount++;
      includeAbsent = true;
      paidToUnpaidCount++;
      absentIndex.push(index);
    }

    // 연속 여부 확인
    if (i > 0 && workdayIndecies[i] === workdayIndecies[i - 1] + 1) {
      consecutiveCount++;
    } else {
      consecutiveCount = 1;
      // includeAbsent = false;
    }

    // 5일 연속 근무 시 처리
    if (consecutiveCount === 5) {
      const topIdx = workdayIndecies[i - 4];
      const bottomIdx = workdayIndecies[i];

      if (includeAbsent) {
        absentGroupTopIndex.push(topIdx);
        absentGroupMiddleIndex.push(
          workdayIndecies[i - 3],
          workdayIndecies[i - 2],
          workdayIndecies[i - 1]
        );
        absentGroupBottomIndex.push(bottomIdx);
      } else {
        groupTopIndex.push(topIdx);
        groupMiddleIndex.push(
          workdayIndecies[i - 3],
          workdayIndecies[i - 2],
          workdayIndecies[i - 1]
        );
        groupBottomIndex.push(bottomIdx);
      }

      const bottomDate = new Date(datas[bottomIdx].date);
      const dayOfWeek = bottomDate.getDay(); // 0: 일요일 ~ 6: 토요일

      const daysUntilSunday = (7 - dayOfWeek) % 7; // 일요일까지 남은 날짜
      const sundayDate = new Date(bottomDate);
      sundayDate.setDate(sundayDate.getDate() + daysUntilSunday); // 같은 주 일요일

      const sundayIndex = datas.findIndex((d) => {
        return (
          normalizeDate(new Date(d.date)).getTime() ===
          normalizeDate(sundayDate).getTime()
        );
      });

      if (sundayIndex !== -1) {
        if (includeAbsent) {
          absentSundayIndexList.push(sundayIndex);
        } else {
          paidSundayIndexList.push(sundayIndex);
        }
      }

      consecutiveCount = 0;
      includeAbsent = false;
    }
  });

  // 🔥 첫 번째 일요일 처리
  const firstSundayIndex = datas.findIndex((d) => {
    const date = new Date(d.date);
    const year = Number(wrapper.year);
    const month = Number(wrapper.month) - 1;
    return (
      date.getDay() === 0 &&
      date.getFullYear() === year &&
      date.getMonth() === month
    );
  });

  if (firstSundayIndex !== -1) {
    const weekStartIndex = Math.max(firstSundayIndex - 6, 0);
    const weekdaysInSameWeek = datas
      .slice(weekStartIndex, firstSundayIndex + 1)
      .filter((d) => {
        const day = new Date(d.date).getDay();
        return day !== 0 && day !== 6 && d.schedule !== '휴일';
      });

    if (weekdaysInSameWeek.length < 5) {
      const sunday = datas[firstSundayIndex];
      const isAbsent =
        sunday.clockInStatus === '결근' || sunday.clockOutStatus === '결근';

      if (isAbsent) {
        paidToUnpaidCountList.push(firstSundayIndex);
        absentIndex.push(firstSundayIndex);
        absentSundayIndexList.push(firstSundayIndex);
      } else {
        paidSundayIndexList.push(firstSundayIndex);
      }
    }
  }

  let paidToUnpaidResult = '0';
  paidToUnpaidCount = absentSundayIndexList.length;

  if (paidToUnpaidCount > 0) {
    if (isPartTime) {
      const hourlyWorkHours = getMostFrequentWorkHour(workHoursArray);
      if (hourlyWorkHours !== undefined) {
        paidToUnpaidResult =
          (paidToUnpaidCount * hourlyWorkHours).toString() + '시간';
      } else {
        paidToUnpaidResult = paidToUnpaidCount.toString() + '일';
      }
    } else {
      paidToUnpaidResult = paidToUnpaidCount.toString() + '일';
    }
  }

  return {
    groupTopIndex,
    groupMiddleIndex,
    groupBottomIndex,
    absentGroupTopIndex,
    absentGroupMiddleIndex,
    absentGroupBottomIndex,
    absentIndex,
    groupCount: groupTopIndex.length,
    totalCount:
      datas.length - saturdayCnt - absentSundayIndexList.length - absentCount,
    paidToUnpaidResult,
    paidSundayIndexList, // ✅ 유급 일요일
    absentSundayIndexList, // ✅ 결근 일요일
  };
}

//결근데이터
export function absentDaysFn(wrapper: AttendanceWrapper, isPartTime?: boolean) {
  const datas: AttendanceType[] = wrapper.data;

  //일일근로시간을 찾아내기 위해 '정상근무' 열의 모든 값을 임시로 담아둘 배열
  const workHoursArray: string[] = [];

  const absentDayIndicies: number[] = datas.reduce<number[]>(
    (prev, data, index) => {
      workHoursArray.push(data.workdayWorkTime);
      if (
        data.schedule === '결근' ||
        data.clockInStatus === '결근' ||
        data.clockOutStatus === '결근'
      ) {
        prev.push(index);
      }
      return prev;
    },
    []
  );

  let absentResult: string = '';
  if (absentDayIndicies.length < 1) {
    absentResult = '0';
  } else {
    let absentDayCount = absentDayIndicies.length;
    let hourlyWorkHours = getMostFrequentWorkHour(workHoursArray);

    if (isPartTime) {
      if (hourlyWorkHours !== undefined) {
        absentResult = `${absentDayCount * hourlyWorkHours}시간`;
      } else {
        absentResult = `${absentDayCount}일`;
      }
    } else {
      absentResult = `${absentDayCount}일`;
    }
  }

  return {
    absentDayIndicies: absentDayIndicies,
    absentDayCount: absentDayIndicies.length,
    absentResult: absentResult,
  };
}

//병가
export function sickLeaveHourFn(
  wrapper: AttendanceWrapper,
  isPartTime?: boolean
) {
  //일일근로시간을 찾아내기 위해 '정상근무' 열의 모든 값을 임시로 담아둘 배열
  const workHoursArray: string[] = [];

  //연가로 예상되나, 형식문제로 시간 추출 불가능한 index
  const unvalidSickLeaveHourIndecies: number[] = [];

  let sickLeaveCount: number = 0;

  const datas: AttendanceType[] = wrapper.data;
  const sickLeaveDayIndecies: number[] = datas.reduce<number[]>(
    (prev, data, index) => {
      workHoursArray.push(data.workdayWorkTime);
      if (data.schedule === '병가') {
        //병가 표기를 위한 layout index에는 넣지만, 병가(n시간) 데이터가 있는경우 일 수 카운팅은 하지 않음.
        prev.push(index);
        if (data.changeReason === null || !data.changeReason.includes('병가')) {
          sickLeaveCount++;
        }
      }
      return prev;
    },
    []
  );
  const sickLeaveHourIndecies: number[] = datas.reduce<number[]>(
    (prev, data, index) => {
      if (data.changeReason !== null && data.changeReason.includes('병가')) {
        prev.push(index);
      }
      return prev;
    },
    []
  );
  let sickDay: string | null =
    sickLeaveCount > 0 ? sickLeaveCount.toString() + '일' : null;
  let sickHour: number = 0;
  let sickMinute: number = 0;
  sickLeaveHourIndecies.forEach((index, i) => {
    //공백제거
    const changeReason = datas[index]?.changeReason?.replace(/\s+/g, '');

    const match =
      changeReason &&
      // changeReason.match(/병가\((?:(\d+)시간)?(?:([0-5]?\d)분)?\)/);
      changeReason.match(/병가\((?:(\d+)시간)?(?:([0-5]?\d)분)?/);

    if (match) {
      // 시간 값이 없으면 0으로 설정
      const hours = parseInt(match[1] || '0', 10);
      // 분 값이 없으면 0으로 설정
      const minutes = parseInt(match[2] || '0', 10);
      sickHour += hours;
      sickMinute += minutes;
    } else {
      unvalidSickLeaveHourIndecies.push(index);
    }
  });
  let sickLeaveResult = '';
  // 분을 시간으로 변환하여 합산
  sickHour += Math.floor(sickMinute / 60);
  // 나머지처리
  sickMinute = sickMinute % 60;

  if (sickDay !== null) {
    if (!isPartTime) {
      sickLeaveResult = sickDay;
    } else {
      const hourlyWorkHours = getMostFrequentWorkHour(workHoursArray);
      if (hourlyWorkHours !== undefined) {
        sickHour += Number(sickDay.replace('일', '')) * hourlyWorkHours;
      }
    }
  }

  if (sickHour > 0) {
    if (sickLeaveResult.trim() !== '') {
      sickLeaveResult += ' ' + sickHour.toString() + '시간';
    } else {
      sickLeaveResult += sickHour.toString() + '시간';
    }
  }
  if (sickMinute > 0) sickLeaveResult += ' ' + sickMinute.toString() + '분';
  if (sickLeaveResult === '') sickLeaveResult = '0';
  return {
    sickLeaveDayIndecies: sickLeaveDayIndecies,
    sickLeaveHourIndecies: sickLeaveHourIndecies,
    unvalidSickLeaveHourIndecies: unvalidSickLeaveHourIndecies,
    sickLeaveResult: sickLeaveResult,
  };
}

//연가
export function annualLeaveHourFn(
  wrapper: AttendanceWrapper,
  isPartTime?: boolean
) {
  //일일근로시간을 찾아내기 위해 '정상근무' 열의 모든 값을 임시로 담아둘 배열
  const workHoursArray: string[] = [];

  //연가로 예상되나, 형식문제로 시간 추출 불가능한 index
  const unvalidAnnualLeaveHourIndecies: number[] = [];

  let annualDayCount: number = 0;

  const datas: AttendanceType[] = wrapper.data;
  const annualLeaveDayIndecies: number[] = datas.reduce<number[]>(
    (prev, data, index) => {
      workHoursArray.push(data.workdayWorkTime);
      if (data.schedule === '연가') {
        //연가 표기를 위한 layout index에는 넣지만, 연가(n시간) 데이터가 있는경우 일 수 카운팅은 하지 않음.
        prev.push(index);
        if (data.changeReason === null || !data.changeReason.includes('연가')) {
          annualDayCount++;
        }
      }
      return prev;
    },
    []
  );
  const annualLeaveHourIndecies: number[] = datas.reduce<number[]>(
    (prev, data, index) => {
      if (data.changeReason !== null && data.changeReason.includes('연가')) {
        prev.push(index);
      }
      return prev;
    },
    []
  );
  let annualDay: string | null =
    annualDayCount > 0 ? annualDayCount.toString() + '일' : null;
  let annualHour: number = 0;
  let annualMinute: number = 0;
  // const regex = /연가\((?:(\d+)시간)?(?:([0-5]?\d)분)?/g;
  annualLeaveHourIndecies.forEach((index, i) => {
    // 공백 제거
    const changeReason = datas[index]?.changeReason?.replace(/\s+/g, '');

    // 첫 번째 정규식: '연가(2시간 30분)' 형식 처리
    const regex1 = /연가\((?:(\d+)시간)?(?:([0-5]?\d)분)?/g;
    let match;

    // 여러 번의 매칭을 찾아 처리
    while ((match = regex1.exec(changeReason)) !== null) {
      // 시간 값이 없으면 0으로 설정
      const hours = parseInt(match[1] || '0', 10);
      // 분 값이 없으면 0으로 설정
      const minutes = parseInt(match[2] || '0', 10);

      annualHour += hours;
      annualMinute += minutes;
    }

    // 두 번째 정규식: '연가사용(17시~18시)' 형식 처리
    const regex2 = /연가사용\((\d{1,2})시~(\d{1,2})시\)/g;

    // '연가사용' 형식 처리
    while ((match = regex2.exec(changeReason)) !== null) {
      // 시작 시간과 종료 시간
      const startHour = parseInt(match[1], 10);
      const endHour = parseInt(match[2], 10);

      // 종료 시간에서 시작 시간을 빼서 시간 추가
      annualHour += endHour - startHour;
    }

    // 만약 매칭되는 항목이 없으면, 해당 인덱스를 unvalidAnnualLeaveHourIndecies에 추가
    if (!regex1.lastIndex && !regex2.lastIndex) {
      unvalidAnnualLeaveHourIndecies.push(index);
    }
  });
  let annualResult = '';

  // 분을 시간으로 변환하여 합산
  annualHour += Math.floor(annualMinute / 60);
  // 나머지처리
  annualMinute = annualMinute % 60;

  if (annualDay !== null) {
    //시간제 근로자가 아닐경우 '일'수로 계산
    if (!isPartTime) {
      annualResult = annualDay;
    } else {
      //시간제 근로자일 경우 일 수 * 일일 근무시간
      const hourlyWorkHours = getMostFrequentWorkHour(workHoursArray);
      if (hourlyWorkHours !== undefined) {
        annualHour += Number(annualDay.replace('일', '')) * hourlyWorkHours;
      }
    }
  }

  if (annualHour > 0) {
    if (annualResult.trim() !== '') {
      annualResult += ' ' + annualHour.toString() + '시간';
    } else {
      annualResult += annualHour.toString() + '시간';
    }
  }

  if (annualMinute > 0) annualResult += ' ' + annualMinute.toString() + '분';
  if (annualResult === '') annualResult = '0';
  return {
    annualLeaveDayIndecies: annualLeaveDayIndecies,
    annualLeaveHourIndecies: annualLeaveHourIndecies,
    unvalidAnnualLeaveHourIndecies: unvalidAnnualLeaveHourIndecies,
    annualResult: annualResult,
  };
}

//단축근무
export function shortenedWorkHourFn(wrapper: AttendanceWrapper) {
  //단축근무로 예상되나, 형식문제로 시간 추출 불가능한 index
  const unvalidShortenedIndecies: number[] = [];

  const datas: AttendanceType[] = wrapper.data;
  //단축근무시간.
  //근무스케줄이 휴일이 아니며, 출근 혹은 퇴근판정 둘 중 하나는 정상판정이 아니며, 수정사유에 '단축'이 포함되어있어야 할 것.
  const shortenedIndecies: number[] = datas.reduce<number[]>(
    (prev, data, index) => {
      if (
        !data.schedule.startsWith('휴일') &&
        ((data.clockInStatus !== null &&
          data.clockInStatus.trim() !== '' &&
          !data.clockInStatus.includes('정상출근')) ||
          (data.clockOutStatus !== null &&
            !data.clockOutStatus.includes('정상퇴근') &&
            //공백 혹은 null도 '정상퇴근'판정으로 간주. 따라서 조건에 포함
            data.clockOutStatus.trim() !== '')) &&
        data.changeReason !== null &&
        data.changeReason.includes('단축')
      ) {
        prev.push(index);
      }
      return prev;
    },
    []
  );
  let shortenedWorkHour: number = 0;

  shortenedIndecies.forEach((index, i) => {
    //공백제거
    const changeReason = datas[index]?.changeReason?.replace(/\s+/g, '');

    // "단축(n시간)" 형식 검사 및 n 추출
    const match = changeReason && changeReason.match(/단축\((\d+)시간/);

    if (match) {
      shortenedWorkHour += parseInt(match[1], 10); // n 값을 숫자로 변환 후 반환
    } else {
      unvalidShortenedIndecies.push(index);
    }
  });

  return {
    shortenedIndecies: shortenedIndecies,
    unvalidShortenedIndecies: unvalidShortenedIndecies,
    shortenedWorkHour: shortenedWorkHour,
  };
}

//연장근무
export function overworkHourFn(wrapper: AttendanceWrapper) {
  //연장근무로 예상되나, 형식문제로 시간 추출 불가능한 index
  const unvalidOverworkIndecies: number[] = [];

  const datas: AttendanceType[] = wrapper.data;
  //연장근무시간.
  //퇴근판정이 연장근무인 인덱스
  const overworkIndecies: number[] = datas.reduce<number[]>(
    (prev, data, index) => {
      if (data.clockOutStatus === '연장근무') {
        prev.push(index);
      }
      return prev;
    },
    []
  );

  let overworkTime: number = 0;
  let overworkMinutes: number = 0; // 분을 따로 저장

  overworkIndecies.forEach((index, i) => {
    const isWeekday = datas[index]?.schedule.startsWith('평일');

    let currentOverworkHours = 0; // 현재 행의 총 연장 시간
    let currentOverworkMinutes = 0; // 현재 행의 총 연장 분

    // 공백 제거
    const changeReason = datas[index]?.changeReason?.replace(/\s+/g, '');

    // "연장 미인정" 검사
    const isNotRecognized = changeReason && changeReason.includes('연장미인정');

    if (isNotRecognized) {
      currentOverworkHours = 0;
      currentOverworkMinutes = 0;
    } else {
      const overWorkMatches = changeReason?.matchAll(
        /연장(?:\(?(\d+)시간\)?)?(?:\(?([0-5]?\d)분\)?)?(인정)?|(?:\(?(\d+)시간\)?)?(?:\(?([0-5]?\d)분\)?)?인정/g
      );
      let hasValidMatch = false;

      for (const match of overWorkMatches ?? []) {
        const hours = match[1] || match[4] ? Number(match[1] || match[4]) : 0;
        const minutes = match[2] || match[5] ? Number(match[2] || match[5]) : 0;

        if (hours > 0 || minutes >= 30) {
          hasValidMatch = true;
        }

        currentOverworkHours += hours;
        if (minutes >= 30) {
          currentOverworkMinutes += 30; // 30분 이상이면 30 추가
        }
      }

      // 만약 n시간 mm분 정규식이 매칭되지 않았다면 HH:mm 검사 수행
      if (!hasValidMatch) {
        const timeMatch = datas[index]?.overWorkTime.match(/^(\d+):(\d+)$/);
        if (timeMatch) {
          currentOverworkHours += Number(timeMatch[1]); // 시간 추가
          const extractedMinutes = Number(timeMatch[2]);
          if (extractedMinutes >= 30) {
            currentOverworkMinutes += 30; // 30분 이상이면 30 추가
          }
        }
      }
    }

    // 평일/휴일에 따라 제한 적용
    if (isWeekday) {
      currentOverworkHours = Math.min(currentOverworkHours, 2);
    } else {
      currentOverworkHours = Math.min(currentOverworkHours, 8);
    }

    // 총 연장 시간 누적
    overworkTime += currentOverworkHours;
    overworkMinutes += currentOverworkMinutes;

    // 유효하지 않은 데이터 처리
    if (currentOverworkHours === 0 && currentOverworkMinutes === 0) {
      unvalidOverworkIndecies.push(index);
    }
  });

  // 마지막에 총 분을 시간으로 변환 (예: 60분 -> 1시간 추가)
  overworkTime += Math.floor(overworkMinutes / 60);
  overworkMinutes = overworkMinutes % 60; // 남은 분 유지

  return {
    overworkTime,
    overworkMinutes,
    overworkIndicies: overworkIndecies,
    unvalidOverworkIndecies,
  };
}
